import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "backup";

// Fetch All Backups
export const fetchBackups = createAsyncThunk(
  "backups/fetchBackups",
  async (
    {
      limit,
      offset,
      search = "",
      searchType = "",
      sortBy = "",
      sortOrder = "",
    },
    { rejectWithValue },
  ) => {
    try {
      let url = `${baseUrl}/get-all?limit=${limit}&offset=${offset}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (searchType) {
        url += `&search_type=${encodeURIComponent(searchType)}`;
      }
      if (sortBy) {
        url += `&sort_by=${encodeURIComponent(sortBy)}`;
      }
      if (sortOrder) {
        url += `&sort_order=${encodeURIComponent(sortOrder)}`;
      }

      const response = await axios.get(url);

      // Extract total from response - check multiple possible locations
      const totalBackups =
        response.data?.pagination?.total ||
        response.data?.total ||
        response.data?.totalBackups ||
        response.data?.count ||
        (Array.isArray(response.data?.results)
          ? response.data.results.length
          : Array.isArray(response.data?.backups)
            ? response.data.backups.length
            : Array.isArray(response.data)
              ? response.data.length
              : 0);

      return {
        backups:
          response.data.results || response.data.backups || response.data || [],
        totalBackups: totalBackups,
        lastBackupDate: response.data?.last_backupdate || null,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching backups");
    }
  },
);

// Get Backup by ID
export const fetchBackupById = createAsyncThunk(
  "backups/fetchBackupById",
  async (backupId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${backupId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching backup");
    }
  },
);

// Create Backup
export const createBackup = createAsyncThunk(
  "backups/createBackup",
  async (backupData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create`, backupData);
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || response.data.error || "Operation failed",
        );
      }
      return response.data;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(
          error.response.data.message ||
            error.response.data.error ||
            "Operation failed",
        );
      }
      return rejectWithValue(error.response?.data || "Error creating backup");
    }
  },
);

// Restore Backup
export const restoreBackup = createAsyncThunk(
  "backups/restoreBackup",
  async (restoreData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/restore`, restoreData);
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || response.data.error || "Operation failed",
        );
      }
      return response.data;
    } catch (error) {
      // Check if error response has success: false
      if (error.response?.data && error.response.data.success === false) {
        return rejectWithValue(
          error.response.data.message ||
            error.response.data.error ||
            "Operation failed",
        );
      }
      return rejectWithValue(error.response?.data || "Error restoring backup");
    }
  },
);

// Delete Backup
export const deleteBackup = createAsyncThunk(
  "backups/deleteBackup",
  async (backupId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${baseUrl}/delete/${backupId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting backup");
    }
  },
);

// Download Backup
export const downloadBackup = createAsyncThunk(
  "backups/downloadBackup",
  async (backupId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/download/${backupId}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error downloading backup",
      );
    }
  },
);

// Get Auto-Backup Settings
export const fetchAutoBackupSettings = createAsyncThunk(
  "backups/fetchAutoBackupSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/auto-backup-settings`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching auto-backup settings",
      );
    }
  },
);

// Update Auto-Backup Settings
export const updateAutoBackupSettings = createAsyncThunk(
  "backups/updateAutoBackupSettings",
  async (settings, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/auto-backup-settings`,
        settings,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error updating auto-backup settings",
      );
    }
  },
);

// Export Backup CSV
export const exportBackupCSV = createAsyncThunk(
  "backups/exportBackupCSV",
  async (appTimezone, { rejectWithValue }) => {
    try {
      await AuditTrailForExport({
        apiUrl: `${baseUrl}/export`,
        file_name: "backup-data-" + Date.now() + ".csv",
        action: "EXPORT_BACKUP_DATA",
        action_to_show: "Backup data exported as CSV",
        additionalData: {
          time_zone: appTimezone
        }
      });
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error exporting backup CSV",
      );
    }
  },
);

const backupSlice = createSlice({
  name: "backups",
  initialState: {
    backups: [],
    totalBackups: 0,
    lastBackupDate: null,
    loading: false,
    error: null,
    autoBackupSettings: {
      frequency: "Daily",
      time: "10",
      period: "AM",
      backupsToKeep: 10,
    },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAutoBackupSettings: (state, action) => {
      state.autoBackupSettings = {
        ...state.autoBackupSettings,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Backups
      .addCase(fetchBackups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBackups.fulfilled, (state, action) => {
        state.loading = false;
        state.backups = action.payload.backups;
        state.totalBackups = action.payload.totalBackups;
        state.lastBackupDate = action.payload.lastBackupDate || null;
      })
      .addCase(fetchBackups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Fetch Backup by ID
      .addCase(fetchBackupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBackupById.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchBackupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Create Backup
      .addCase(createBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBackup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Restore Backup
      .addCase(restoreBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreBackup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(restoreBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Delete Backup
      .addCase(deleteBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBackup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Download Backup
      .addCase(downloadBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadBackup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Fetch Auto-Backup Settings
      .addCase(fetchAutoBackupSettings.fulfilled, (state, action) => {
        if (action.payload) {
          state.autoBackupSettings = {
            ...state.autoBackupSettings,
            ...action.payload,
          };
        }
      })
      // Update Auto-Backup Settings
      .addCase(updateAutoBackupSettings.fulfilled, (state, action) => {
        if (action.payload) {
          state.autoBackupSettings = {
            ...state.autoBackupSettings,
            ...action.payload,
          };
        }
      })
      // Export Backup CSV
      .addCase(exportBackupCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportBackupCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportBackupCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearError, setAutoBackupSettings } = backupSlice.actions;
export default backupSlice.reducer;
